import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { ThemeProvider, ToastProvider } from '@viquard/doc-ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@viquard/doc-ui/tokens.css';
import '@viquard/doc-ui/components.css';
import '@viquard/doc-ui/icons.css';
import '@viquard/doc-ui/forms.css';
import '@viquard/doc-ui/layout.css';
import '@viquard/doc-ui/a11y.css';
import '@viquard/calendar/styles.css';
import '@/lib/i18n';
import { createQueryClient } from '@/lib/query-client';
import { routeTree } from '@/route-tree.gen';
import '@/animations/app-motion.css';
import '@/styles/globals.css';
import '@/features/dashboard/dashboard.css';
// Feature stylesheets — loaded in the SAME order they previously
// appeared inside `features/articles/articles.css` (annotations →
// reactions → bookmarks → comments). CSS cascade order is sensitive
// to import order, so the four extracted stylesheets are pinned
// here right after the article one to preserve any specificity ties
// that depended on declaration position. A future refactor that
// scopes selectors more tightly may relax this constraint.
import '@/features/articles/articles.css';
import '@/features/annotations/annotations.css';
import '@/features/reactions/reactions.css';
import '@/features/bookmarks/bookmarks.css';
import '@/features/comments/comments.css';
import '@/features/groups/groups.css';
import '@/features/forum/forum.css';
import '@/features/calendar/calendar.css';
import '@/features/scheduling/scheduling.css';
import '@/features/coordination/coordination.css';
import '@/features/files/files.css';
import '@/features/members/members.css';
import '@/features/surveys/surveys.css';
import '@/features/guide/guide.css';
import '@/styles/app-layout.css';

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {},
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

createRoot(container).render(
  <StrictMode>
    <ThemeProvider storageKey="personal-blog.theme">
      {/* Single toast surface for the whole app. Auth flows, save dialogs,
          background workers — every error / hint funnels through here so
          users see one consistent affordance instead of inline raw codes. */}
      <ToastProvider placement="top-right" max={4}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ToastProvider>
    </ThemeProvider>
  </StrictMode>,
);
